"use client";

import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { Heart } from "lucide-react";

type FavoriteButtonProps = {
  isFavorite: boolean;
  pending: boolean;
  onToggle: () => void;
  tone?: "image" | "surface";
  className?: string;
};

export default function FavoriteButton({
  isFavorite,
  pending,
  onToggle,
  tone = "surface",
  className,
}: FavoriteButtonProps): JSX.Element {
  const label = isFavorite
    ? "Remove hotel from favorites"
    : "Save hotel to favorites";

  return (
    <Button
      type="button"
      variant="ghost"
      size="icon"
      className={cn(
        "relative h-11 w-11 rounded-none bg-transparent shadow-none hover:bg-transparent",
        tone === "image"
          ? isFavorite
            ? "text-destructive hover:text-destructive"
            : "text-pure-white hover:text-pure-white"
          : "text-foreground hover:text-foreground",
        tone === "surface" && isFavorite && "text-destructive",
        className
      )}
      aria-label={label}
      aria-pressed={isFavorite}
      aria-busy={pending}
      disabled={pending}
      onPointerDown={(event) => event.stopPropagation()}
      onClick={(event) => {
        event.stopPropagation();
        onToggle();
      }}
    >
      <Heart
        aria-hidden
        data-testid="favorite-heart"
        className="h-5 w-5"
        fill={
          tone === "image"
            ? isFavorite
              ? "hsl(var(--destructive))"
              : "hsl(var(--muted-foreground))"
            : isFavorite
              ? "currentColor"
              : "none"
        }
        stroke={tone === "image" ? "hsl(var(--pure-white))" : "currentColor"}
        strokeWidth={tone === "image" ? 2.5 : 2}
      />
    </Button>
  );
}
